import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { hasAnyRole } from "@/lib/roles";
import { useIsMobile } from "@/hooks/useMobile";
import { Plus, Pencil, Trash2, Dumbbell } from "lucide-react";
import { useState, CSSProperties } from "react";
import { toast } from "sonner";

interface ServiceFormData {
  name: string;
  description: string;
  color: string;
  isActive: boolean;
}

const DEFAULT_COLORS = [
  "#7AB51D", // Primary green
  "#F7941D", // Orange
  "#3B82F6", // Blue
  "#8B5CF6", // Purple
  "#EC4899", // Pink
  "#14B8A6", // Teal
  "#F59E0B", // Amber
  "#EF4444", // Red
];

// ServiceForm component - extracted to prevent re-creation on each render
const ServiceForm = ({ 
  isEdit = false, 
  formData, 
  setFormData 
}: { 
  isEdit?: boolean;
  formData: ServiceFormData;
  setFormData: (data: ServiceFormData) => void;
}) => (
  <div className="space-y-4">
    <div className="space-y-2">
      <Label htmlFor="name">Name</Label>
      <Input
        id="name"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        placeholder="z.B. Krafttraining"
      />
    </div>
    
    <div className="space-y-2">
      <Label htmlFor="description">Beschreibung</Label>
      <Textarea
        id="description"
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        placeholder="Optionale Beschreibung des Services"
        rows={3}
      />
    </div>

    <div className="space-y-2">
      <Label>Farbe</Label>
      <div className="flex flex-wrap gap-2">
        {DEFAULT_COLORS.map(color => (
          <button
            key={color}
            type="button"
            className={`w-8 h-8 rounded-full border-2 transition-all ${
              formData.color === color ? "border-foreground scale-110" : "border-transparent"
            }`}
            style={{ backgroundColor: color }}
            onClick={() => setFormData({ ...formData, color })}
          />
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <Label htmlFor="customColor" className="text-sm">Benutzerdefiniert:</Label>
        <Input
          id="customColor"
          type="color"
          value={formData.color}
          onChange={(e) => setFormData({ ...formData, color: e.target.value })}
          className="w-12 h-8 p-0 border-0"
        />
        <span className="text-sm text-muted-foreground">{formData.color}</span>
      </div>
    </div>

    {isEdit && (
      <div className="flex items-center justify-between">
        <Label htmlFor="isActive">Aktiv</Label>
        <Switch
          id="isActive"
          checked={formData.isActive}
          onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
        />
      </div>
    )}
  </div>
);

export default function GymServices() {
  const { loading, user } = useAuth();
  const { data: config } = trpc.config.getTenantConfig.useQuery();
  const isMobile = useIsMobile();
  
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingService, setEditingService] = useState<number | null>(null);
  const [formData, setFormData] = useState<ServiceFormData>({
    name: "",
    description: "",
    color: DEFAULT_COLORS[0],
    isActive: true
  });

  const utils = trpc.useUtils();
  const { data: services, isLoading } = trpc.gym.services.list.useQuery({ includeInactive: true });
  
  const createMutation = trpc.gym.services.create.useMutation({
    onSuccess: () => {
      toast.success("Service erstellt");
      utils.gym.services.list.invalidate();
      setIsCreateOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const updateMutation = trpc.gym.services.update.useMutation({
    onSuccess: () => {
      toast.success("Service aktualisiert");
      utils.gym.services.list.invalidate();
      setEditingService(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const deleteMutation = trpc.gym.services.delete.useMutation({
    onSuccess: () => {
      toast.success("Service gelöscht");
      utils.gym.services.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      color: DEFAULT_COLORS[0],
      isActive: true
    });
  };

  const handleCreate = () => {
    createMutation.mutate({
      name: formData.name,
      description: formData.description || undefined,
      color: formData.color
    });
  };

  const handleUpdate = () => {
    if (!editingService) return;
    updateMutation.mutate({
      id: editingService,
      name: formData.name,
      description: formData.description || undefined,
      color: formData.color,
      isActive: formData.isActive
    });
  };

  const handleEdit = (service: NonNullable<typeof services>[0]) => {
    setFormData({
      name: service.name,
      description: service.description || "",
      color: service.color,
      isActive: service.isActive
    });
    setEditingService(service.id);
  };

  const handleDelete = (id: number) => {
    if (confirm("Möchten Sie diesen Service wirklich löschen?")) {
      deleteMutation.mutate({ id });
    }
  };

  // ServiceForm component now extracted above

  // Auth check
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Laden...</div>;
  }

  if (!user || !hasAnyRole(user.roles, ["admin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <h1 className="text-2xl font-semibold tracking-tight text-center">
            Anmeldung erforderlich
          </h1>
          <p className="text-sm text-muted-foreground text-center max-w-sm">
            Der Zugriff auf diesen Bereich erfordert Administratorrechte.
          </p>
          <Button
            onClick={() => {
              window.location.href = getLoginUrl(config?.authProvider);
            }}
            size="lg"
            className="w-full"
          >
            Anmelden
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider
      style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}
    >
      <AdminSidebar setSidebarWidth={setSidebarWidth} />
      <SidebarInset>
        {isMobile && (
          <header className="flex h-14 items-center gap-2 border-b px-4">
            <SidebarTrigger />
          </header>
        )}
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Gym Services</h1>
              <p className="text-muted-foreground">
                Verwalten Sie die verfügbaren Trainingsarten
              </p>
            </div>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm}>
                  <Plus className="h-4 w-4 mr-2" />
                  Neuer Service
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Neuen Service erstellen</DialogTitle>
                  <DialogDescription>
                    Erstellen Sie eine neue Trainingsart für Ihr Fitnessstudio
                  </DialogDescription>
                </DialogHeader>
                <ServiceForm formData={formData} setFormData={setFormData} />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button 
                    onClick={handleCreate}
                    disabled={!formData.name || createMutation.isPending}
                  >
                    {createMutation.isPending ? "Erstelle..." : "Erstellen"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Services List */}
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Laden...</div>
          ) : services?.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Dumbbell className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">Noch keine Services vorhanden</p>
                <Button onClick={() => setIsCreateOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Ersten Service erstellen
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {services?.map(service => (
                <Card key={service.id} className={!service.isActive ? "opacity-60" : ""}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: service.color }}
                        />
                        <CardTitle className="text-lg">{service.name}</CardTitle>
                      </div>
                      {!service.isActive && (
                        <span className="text-xs bg-muted px-2 py-1 rounded">Inaktiv</span>
                      )}
                    </div>
                    {service.description && (
                      <CardDescription className="mt-2">
                        {service.description}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex gap-2">
                      <Dialog open={editingService === service.id} onOpenChange={(open) => {
                        if (!open) {
                          setEditingService(null);
                          resetForm();
                        }
                      }}>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEdit(service)}
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Bearbeiten
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Service bearbeiten</DialogTitle>
                            <DialogDescription>
                              Bearbeiten Sie die Details des Services
                            </DialogDescription>
                          </DialogHeader>
                          <ServiceForm isEdit formData={formData} setFormData={setFormData} />
                          <DialogFooter>
                            <Button variant="outline" onClick={() => {
                              setEditingService(null);
                              resetForm();
                            }}>
                              Abbrechen
                            </Button>
                            <Button 
                              onClick={handleUpdate}
                              disabled={!formData.name || updateMutation.isPending}
                            >
                              {updateMutation.isPending ? "Speichere..." : "Speichern"}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(service.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
